import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { monadClient, type PlaceDetails } from '../api/client'
import { controlChrome } from '../widgets/editorChrome'
import { newSessionToken } from './places'

const MIN_QUERY = 2
const DEBOUNCE_MS = 250

/**
 * "Search for a place or address" — autocomplete as you type, pick a
 * suggestion, receive the resolved place. One session token covers a search
 * session (every keystroke's suggestions and the closing details call) and is
 * renewed after each pick, as Sentinel's Site form did. Renders nothing about
 * configuration: the caller shows it only when places is configured.
 */
export function PlaceSearch({ slug, onPick }: { slug: string; onPick: (place: PlaceDetails) => void }) {
  const [text, setText] = useState('')
  const [query, setQuery] = useState('')
  const [resolving, setResolving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const tokenRef = useRef<string>(newSessionToken())

  useEffect(() => {
    const t = setTimeout(() => setQuery(text.trim()), DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [text])

  const { data, isFetching, isError } = useQuery({
    queryKey: ['places-autocomplete', slug, query],
    queryFn: () => monadClient.autocompletePlaces(slug, query, tokenRef.current),
    enabled: query.length >= MIN_QUERY,
    staleTime: 60_000,
    retry: false,
  })
  const predictions = query.length >= MIN_QUERY ? data?.predictions ?? [] : []

  async function pick(placeId: string) {
    setResolving(placeId)
    setError(null)
    try {
      const place = await monadClient.getPlaceDetails(slug, placeId, tokenRef.current)
      tokenRef.current = newSessionToken()
      setText('')
      setQuery('')
      onPick(place)
    } catch {
      setError('Could not fetch this place. Try again.')
    } finally {
      setResolving(null)
    }
  }

  return (
    <div data-testid="place-search" className="mb-2">
      <input
        data-testid="place-search-input"
        type="search"
        autoComplete="off"
        placeholder="Search for a place or address"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Enter must not save the half-typed manual address below.
          if (e.key === 'Enter') {
            e.preventDefault()
            if (predictions[0]) void pick(predictions[0].place_id)
          }
        }}
        className={`w-full rounded-sm bg-background px-2 py-1.5 text-sm outline-none ${controlChrome('form')}`}
      />
      {query.length >= MIN_QUERY && (
        <div data-testid="place-search-results" className="mt-1 rounded-sm border border-border">
          {predictions.map((p) => (
            <button
              key={p.place_id}
              type="button"
              data-testid={`place-option-${p.place_id}`}
              disabled={resolving !== null}
              onClick={() => void pick(p.place_id)}
              className="block w-full px-2 py-1.5 text-left text-sm hover:bg-muted disabled:opacity-50"
            >
              <span className="block truncate font-medium">{p.main_text || p.description}</span>
              {p.secondary_text && (
                <span className="block truncate text-xs text-muted-foreground">{p.secondary_text}</span>
              )}
            </button>
          ))}
          {predictions.length === 0 && !isFetching && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground">
              {isError ? 'The place search is unavailable right now.' : 'No places found.'}
            </div>
          )}
          {(isFetching || resolving) && (
            <div className="px-2 py-1 text-xs text-muted-foreground">
              {resolving ? 'Fetching the address and opening hours…' : 'Searching…'}
            </div>
          )}
          <div className="border-t border-border px-2 py-0.5 text-[10px] text-muted-foreground">Powered by Google</div>
        </div>
      )}
      {error && <div data-testid="place-search-error" className="mt-1 text-[11px] text-destructive">{error}</div>}
    </div>
  )
}
